// src/admin/components/common/DataTable.jsx

import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ListFilterBar from './ListFilterBar';
import { GUTTER } from '../layout/WorkspaceView';
import useIsNarrowViewport from '../../../shared/hooks/useIsNarrowViewport';

// Where a column goes in a stacked row. Declared here so the table and the stacked list cannot
// disagree about what a role means.
const ROLE_PRIMARY = 'primary';
const ROLE_SECONDARY = 'secondary';
const ROLE_META = 'meta';

/** The visible cell value for a row, however the column chose to produce it. */
function cellValue(col, row) {
  return typeof col.render === 'function' ? col.render(row) : row[col.key];
}

/** Every action this row actually offers. */
function visibleActions(actions, row) {
  return actions.filter(action => (typeof action.visible === 'function' ? action.visible(row) : true));
}

// The three-dot menu. Shared by the table's action cell and the stacked block, so a row offers
// the same things whichever shape it is in.
function RowActionsMenu({ actions, row, align = 'end' }) {
  const available = visibleActions(actions, row);
  if (available.length === 0) return null;

  return (
    <div className="dropdown">
      <button
        className="btn btn-link p-0 text-muted"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        title="Row actions"
        aria-label="Row actions"
      >
        <i className="bi bi-three-dots-vertical" />
      </button>
      <ul className={classNames('dropdown-menu', align === 'end' && 'dropdown-menu-end')}>
        {available.map(action => (
          <li key={action.key}>
            <button
              className="dropdown-item"
              onClick={() => action.onClick(row)}
              disabled={typeof action.disabled === 'function' ? action.disabled(row) : false}
            >
              {action.icon && <i className={`bi ${action.icon} me-2`} />}
              {action.label}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

RowActionsMenu.propTypes = {
  actions: PropTypes.array.isRequired,
  row: PropTypes.object.isRequired,
  align: PropTypes.oneOf(['start', 'end']),
};

/**
 * columns: [
 *   {
 *     key: "name",
 *     label: "Name",
 *     icon: "bi-person",         // Optional: Bootstrap icon class
 *     tooltip: "Full Name",      // Optional: Tooltip text (shows on hover)
 *     sortable: true,
 *     render: (row) => row.name,
 *     className: "",
 *     role: "primary",           // Narrow screens only — see below
 *     hideOnNarrow: false,
 *   },
 *   ...
 * ]
 *
 * ## Narrow screens
 *
 * Below `md` a table cannot be read: eight columns in 390px either truncate to nothing or
 * scroll sideways. So under that width the same rows render as a stacked list instead, one
 * block per row, and `role` says how each column is placed in it:
 *
 *   - `primary`    — the block's heading. Defaults to the first column if none is declared.
 *   - `secondary`  — a muted line under the heading, for the things that qualify it (a version,
 *                    a type, a date). Several are joined with `·`.
 *   - `meta`       — a muted inline footer at the bottom of the block.
 *   - (none)       — a `label: value` pair in the block's body, which is what every existing
 *                    table gets without being changed.
 *
 * `hideOnNarrow` drops a column from the stacked list entirely, for anything that is noise on a
 * phone. Sorting is not offered below the breakpoint — the header that carries it is gone — so
 * the list keeps whatever order the sort state last produced.
 *
 * The breakpoint is `md` (768px), matching the row-action dropdown below and the sidebar's own
 * collapse, so the whole admin UI changes shape at one width.
 *
 * ## Wide tables: `fill`
 *
 * By default the table is as wide as the page gives it, which for a `container py-4` page means
 * a dozen columns squeezed into 1320px of centered body — cells wrap and rows grow several
 * lines tall. `fill` is the other shape: the table fills the height of the region it is in and
 * owns both of its scrollbars, columns size to their content, and the header row stays put
 * while the rows move. Pair it with `WorkspaceDataView`, which supplies a full-width body that
 * deliberately does not scroll; see docs/dev/admin-layout.md.
 *
 * The pane runs to the edges — square, no card border — so the filter bar above it carries the
 * view's GUTTER instead, which is also what lines the first column up with the page title.
 *
 * It is opt-in because thirteen other tables use this component inside a centered container,
 * where the present behaviour is the right one. With `fill` unset nothing about the rendering
 * changes.
 *
 * A region that scrolls has to be reachable by keyboard — otherwise the columns past the right
 * edge belong to trackpad users only — so under `fill` the wrapper is a focusable
 * `role="region"`. Give it a name with `label`.
 * actions: [
 *   {
 *     key: "edit",
 *     label: "Edit",
 *     icon: "bi-pencil-fill",
 *     onClick: (row) => {},
 *   },
 *   ...
 * ]
 */

// One row, stacked, for a screen too narrow to read a table on.
//
// The shape is deliberately the same as the task and asset lists the admin UI already uses: a
// heading, a muted line qualifying it, then the detail as label/value pairs. A column that
// declared no role lands in that body, which is why thirteen existing tables became readable
// here without being touched.
function StackedRow({ columns, actions, row, onRowClick, isSelected }) {
  const shown = columns.filter(col => !col.hidden && !col.hideOnNarrow);

  // The heading. Without an explicit `primary` the first column is it — in every table in this
  // app that is the name, which is what you look for first in a list.
  const primary = shown.find(col => col.role === ROLE_PRIMARY) || shown[0];
  const secondary = shown.filter(col => col.role === ROLE_SECONDARY);
  const meta = shown.filter(col => col.role === ROLE_META);
  const pairs = shown.filter(
    col => col !== primary && !col.role
  );

  const clickable = typeof onRowClick === 'function';

  return (
    <li
      className={classNames(
        'list-group-item px-2 py-3',
        isSelected && 'active-subtle',
        clickable && 'cursor-pointer'
      )}
      style={clickable ? { cursor: 'pointer' } : undefined}
      onClick={clickable ? () => onRowClick(row) : undefined}
    >
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1 min-w-0">
          {primary && <div className="fw-semibold">{cellValue(primary, row)}</div>}

          {secondary.length > 0 && (
            <div className="text-muted small d-flex flex-wrap align-items-center gap-1">
              {secondary.map((col, index) => (
                <React.Fragment key={col.key}>
                  {index > 0 && <span aria-hidden>·</span>}
                  <span>{cellValue(col, row)}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Stops a row-click from firing when the menu is what was tapped. */}
        {actions.length > 0 && (
          <div
            className="flex-shrink-0"
            onClick={clickable ? (e) => e.stopPropagation() : undefined}
          >
            <RowActionsMenu actions={actions} row={row} />
          </div>
        )}
      </div>

      {pairs.length > 0 && (
        <dl className="row row-cols-1 g-0 mb-0 mt-2 small">
          {pairs.map(col => (
            <div className="col d-flex gap-2 py-1" key={col.key}>
              <dt className="text-muted fw-normal flex-shrink-0" style={{ minWidth: '7.5rem' }}>
                {col.label}
              </dt>
              <dd className="mb-0 min-w-0">{cellValue(col, row)}</dd>
            </div>
          ))}
        </dl>
      )}

      {meta.length > 0 && (
        <div className="text-muted small mt-2 d-flex flex-wrap align-items-center gap-1">
          {meta.map((col, index) => (
            <React.Fragment key={col.key}>
              {index > 0 && <span aria-hidden>·</span>}
              <span>{cellValue(col, row)}</span>
            </React.Fragment>
          ))}
        </div>
      )}
    </li>
  );
}

StackedRow.propTypes = {
  columns: PropTypes.array.isRequired,
  actions: PropTypes.array.isRequired,
  row: PropTypes.object.isRequired,
  onRowClick: PropTypes.func,
  isSelected: PropTypes.bool,
};

export default function DataTable({
  items,
  columns,
  actions = [],
  filterPlaceholder = "Search…",
  emptyMessage = "No results found.",
  className = "",
  filterExtras = null,
  hideFilter = false,
  getRowKey,
  onRowClick,
  selectedRowKey,
  tableClassName = "",
  renderAfterRow,
  filterSummary,
  fill = false,
  label = 'Table',
}) {
  // --- Local State ---
  const isNarrow = useIsNarrowViewport();
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState(() => {
    // Default to first sortable column
    const firstSortable = columns.find(col => col.sortable);
    return firstSortable ? firstSortable.key : columns[0].key;
  });
  const [sortDir, setSortDir] = useState('asc');

  // --- Filter + Sort ---
  const filtered = useMemo(() => {
    let filteredItems = items;

    // Generic filter across all visible fields
    if (q.trim()) {
    const query = q.toLowerCase();
    filteredItems = filteredItems.filter(row =>
      columns.some(col => {
        // Skip if column is not intended for rendering
        if (col.hidden) return false;

        let val;

        if (typeof col.filterValue === 'function') {
          val = col.filterValue(row);
        } else {
          val = row[col.key];
        }

        if (val != null && typeof val === 'object') return false;
        return String(val ?? '').toLowerCase().includes(query);
      })
    );
  }

    // Sort
    const sortCol = columns.find(col => col.key === sortKey);
    if (sortCol && sortCol.sortable) {
      filteredItems = [...filteredItems].sort((a, b) => {
        // Prefer primitive value from row[col.key], else fallback to filterValue, then render
        let va = a[sortKey];
        let vb = b[sortKey];

        // If not primitive, try filterValue
        if (
          (va === undefined || va === null || typeof va === 'object') &&
          typeof sortCol.filterValue === 'function'
        ) {
          va = sortCol.filterValue(a);
        }
        if (
          (vb === undefined || vb === null || typeof vb === 'object') &&
          typeof sortCol.filterValue === 'function'
        ) {
          vb = sortCol.filterValue(b);
        }

        // If still not primitive, try render
        if (
          (va === undefined || va === null || typeof va === 'object') &&
          typeof sortCol.render === 'function'
        ) {
          va = sortCol.render(a);
        }
        if (
          (vb === undefined || vb === null || typeof vb === 'object') &&
          typeof sortCol.render === 'function'
        ) {
          vb = sortCol.render(b);
        }

        // Try numeric, else string compare
        if (!isNaN(Number(va)) && !isNaN(Number(vb))) {
          va = Number(va); vb = Number(vb);
          return sortDir === 'asc' ? va - vb : vb - va;
        } else {
          va = String(va ?? '').toLowerCase();
          vb = String(vb ?? '').toLowerCase();
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
      });
    }

    return filteredItems;
  }, [items, columns, q, sortKey, sortDir]);

  // --- Handlers ---
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const resolveRowKey = (row, index) => {
    if (typeof getRowKey === 'function') return getRowKey(row, index);
    return row.id ?? row.key ?? row.media_id ?? index;
  };

  // --- Initialize Bootstrap tooltips (scoped to this table) ---
  const tableWrapperRef = React.useRef(null);
  useEffect(() => {
    const root = tableWrapperRef.current;
    if (!root || typeof window === 'undefined' || !window.bootstrap?.Tooltip) return;

    const tooltipTriggerList = root.querySelectorAll('[data-bs-toggle="tooltip"]');
    const tooltipList = [...tooltipTriggerList].map(el => new window.bootstrap.Tooltip(el));

    return () => {
      tooltipList.forEach(tooltip => tooltip.dispose());
    };
  }, [columns, filtered]);

  // --- Rendering ---
  return (
    // `fill` makes this a flex column that takes the height of its region, so the scrolling
    // belongs to the rows rather than to the page. `minHeight: 0` is what allows the scroll
    // region below to be shorter than its content.
    <div
      className={classNames(className, fill && 'flex-grow-1 d-flex flex-column')}
      style={fill ? { minHeight: 0, minWidth: 0 } : undefined}
    >
      {/* ---- Filter bar: search first (same markup as list-only tables), then optional extras ---- */}
      {!hideFilter && (
        <ListFilterBar
          inputId={filterExtras ? 'data-table-filter-search' : undefined}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={filterPlaceholder}
          extras={filterExtras}
          summary={filterSummary ? filterSummary(filtered.length, items.length) : null}
          className={fill ? `flex-shrink-0 ${GUTTER} pt-3` : ''}
        />
      )}

      {/* ---- Rows: a table when there is room for one, a stacked list when there is not ---- */}
      {isNarrow ? (
        <div
          className={classNames(fill
            ? `flex-grow-1 overflow-auto mb-0 ${GUTTER}`
            : (hideFilter ? 'mb-0' : 'mb-3'))}
          style={fill ? { minHeight: 0 } : undefined}
        >
          {filtered.length > 0 ? (
            <ul className="list-group list-group-flush">
              {filtered.map((row, index) => {
                const rowKey = resolveRowKey(row, index);
                return (
                  <React.Fragment key={rowKey}>
                    <StackedRow
                      columns={columns}
                      actions={actions}
                      row={row}
                      onRowClick={onRowClick}
                      isSelected={selectedRowKey != null && selectedRowKey === rowKey}
                    />
                    {typeof renderAfterRow === 'function'
                      ? renderAfterRow(row, {
                        selected: selectedRowKey != null && selectedRowKey === rowKey,
                        stacked: true,
                      })
                      : null}
                  </React.Fragment>
                );
              })}
            </ul>
          ) : (
            <div className="text-center text-muted py-4">{emptyMessage}</div>
          )}
        </div>
      ) : (
      <div
        ref={tableWrapperRef}
        className={classNames(fill
          ? 'table-pane'
          : ['table-wrapper', hideFilter ? 'mb-0' : 'mb-3'])}
        /* Only a filling table scrolls, and a scroll region nobody can focus is one a keyboard
           cannot pan — the columns past the right edge would be trackpad-only. */
        role={fill ? 'region' : undefined}
        aria-label={fill ? label : undefined}
        tabIndex={fill ? 0 : undefined}
      >
        <table className={classNames('table align-middle mb-0', tableClassName)}>
          <thead className="table-light">
            <tr>
              {columns.map(col => (
                <th
                  key={col.key}
                  className={classNames(
                    col.sortable && 'sortable',
                    sortKey === col.key && `sorted-${sortDir}`,
                    col.className
                  )}
                  onClick={col.sortable ? () => handleSort(col.key) : undefined}
                  style={col.style}
                  scope="col"
                >
                  <span 
                    className="d-inline-flex align-items-center"
                    title={col.tooltip || ''}
                    data-bs-toggle={col.tooltip ? 'tooltip' : undefined}
                    data-bs-placement="top"
                  >
                    {col.icon && <i className={`bi ${col.icon} me-1`} />}
                    {col.label}
                    {/* Sort Icon */}
                    {col.sortable && (
                      sortKey === col.key ? (
                        sortDir === 'asc'
                          ? <i className="bi bi-chevron-up ms-1" />
                          : <i className="bi bi-chevron-down ms-1" />
                      ) : (
                        <i className="bi bi-chevron-expand ms-1 opacity-50" />
                      )
                    )}
                  </span>
                </th>
              ))}
              {actions.length > 0 && (
                /* No visible label: a column of buttons says what it is, and the word was
                   wider than the buttons it sat over. */
                <th className="text-end text-nowrap" style={{ width: '1%' }}>
                  <span className="visually-hidden">Actions</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {filtered.length > 0 ? (
              filtered.map((row, index) => {
                const rowKey = resolveRowKey(row, index);
                const isSelected = selectedRowKey != null && selectedRowKey === rowKey;
                return (
                <React.Fragment key={rowKey}>
                <tr
                  className={classNames(isSelected && 'table-active', onRowClick && 'cursor-pointer')}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map(col => (
                    <td key={col.key} className={col.className} style={col.style}>
                      {typeof col.render === 'function'
                        ? col.render(row)
                        : row[col.key]}
                    </td>
                  ))}
                  {actions.length > 0 && (
                    <td className="text-end" onClick={onRowClick ? (e) => e.stopPropagation() : undefined}>
                      {/* The table only renders above `md` now, so the buttons no longer need a
                          `d-none d-md-flex`; the stacked list carries the menu below it. */}
                      <div className="d-flex gap-1 justify-content-end">
                        {visibleActions(actions, row).map(action => (
                          <button
                            key={action.key}
                            className={classNames(
                              'btn btn-sm btn-light border',
                              action.tone === 'danger' && 'text-danger',
                            )}
                            title={action.label}
                            aria-label={action.label}
                            onClick={() => action.onClick(row)}
                            type="button"
                            disabled={typeof action.disabled === 'function' ? action.disabled(row) : false}
                          >
                            {action.icon && <i className={`bi ${action.icon}`} aria-hidden />}
                            {action.text}
                          </button>
                        ))}
                      </div>
                    </td>
                  )}
                </tr>
                {typeof renderAfterRow === 'function' ? renderAfterRow(row, { selected: isSelected }) : null}
                </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length + (actions.length > 0 ? 1 : 0)}>
                  <div className="text-center text-muted py-4">{emptyMessage}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

DataTable.propTypes = {
  items:    PropTypes.arrayOf(PropTypes.object).isRequired,
  columns:  PropTypes.arrayOf(PropTypes.shape({
    key:      PropTypes.string.isRequired,
    label:    PropTypes.string.isRequired,
    icon:     PropTypes.string,     // Optional Bootstrap icon class
    tooltip:  PropTypes.string,     // Optional tooltip text
    sortable: PropTypes.bool,
    render:   PropTypes.func,
    filterValue: PropTypes.func,
    hidden:   PropTypes.bool,
    className:PropTypes.string,
    style:    PropTypes.object,
    /** Placement in a stacked row below `md`. See the note at the top of this file. */
    role:     PropTypes.oneOf([ROLE_PRIMARY, ROLE_SECONDARY, ROLE_META]),
    /** Leave this column out of the stacked row entirely. */
    hideOnNarrow: PropTypes.bool,
  })).isRequired,
  actions:  PropTypes.arrayOf(PropTypes.shape({
    key:      PropTypes.string.isRequired,
    label:    PropTypes.string.isRequired,
    icon:     PropTypes.string,
    onClick:  PropTypes.func,
    visible:  PropTypes.func,
    disabled: PropTypes.func,
    tone:     PropTypes.oneOf(['danger']),   // destructive actions only
    /** A short word in place of an icon, for an action no glyph says clearly (OCR). */
    text:     PropTypes.string,
  })),
  filterPlaceholder: PropTypes.string,
  emptyMessage: PropTypes.string,
  className: PropTypes.string,
  filterExtras: PropTypes.node,
  hideFilter: PropTypes.bool,
  getRowKey: PropTypes.func,
  onRowClick: PropTypes.func,
  selectedRowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tableClassName: PropTypes.string,
  renderAfterRow: PropTypes.func,
  /** (shown, total) => node, rendered at the end of the filter bar. */
  filterSummary: PropTypes.func,
  /** Fill the region's height and scroll inside it, for a table wider than the page. */
  fill: PropTypes.bool,
  /** Accessible name for that scroll region. Say what the table is; "Table" is the fallback. */
  label: PropTypes.string,
};
